import { OpenHouse } from 'declarations/exclusives/exclusiveListingInfo';
import moment, { Moment } from 'moment-timezone';
import take from 'lodash/take';
import orderBy from 'lodash/orderBy';

export interface ExclusiveTourRequest {
  created_at: string;
  fulfilled_at: string | null;
  exclusive_tour_request_time_slots: ExclusiveTourRequestTimeSlot[];
}

export interface ExclusiveTourRequestTimeSlot {
  start_time: string;
  end_time: string;
  selected_time_slot?: boolean;
}

export type ReportEvent = (
  label: string,
  additionalContextualData?: Record<string, unknown>,
) => void;

const FIRST_SLOT_OF_DAY_HOUR = 8;
const SLOT_PERIOD_IN_HOURS = 2;
const AMOUNT_OF_DAYS = 14;
const PERIODS_PER_DAY = 6;

export const DEFAULT_TIMEZONE = 'America/Chicago';
export const SLOT_TIME_FORMAT = 'ha';
export const SLOT_DATE_TIME_FORMAT = `ddd [at] MMM D, ${SLOT_TIME_FORMAT}`;

export function getSlots(
  timezone = DEFAULT_TIMEZONE,
  now = moment.tz(timezone),
): ExclusiveTourRequestTimeSlot[] {
  const slots = [];
  let day = now.set({ hours: FIRST_SLOT_OF_DAY_HOUR, seconds: 0, minutes: 0, milliseconds: 0 });
  for (let dayNumber = 1; dayNumber <= AMOUNT_OF_DAYS; dayNumber++) {
    for (let periodIdx = 0; periodIdx < PERIODS_PER_DAY; periodIdx++) {
      day = day.set({ hours: FIRST_SLOT_OF_DAY_HOUR + periodIdx * SLOT_PERIOD_IN_HOURS });
      slots.push({
        start_time: day.toISOString(),
        end_time: day.clone().add(SLOT_PERIOD_IN_HOURS, 'hours').toISOString(),
      });
    }
    day = day.clone().add(1, 'days');
  }
  return slots;
}

export function getClosestSlots(
  slots: ExclusiveTourRequestTimeSlot[],
  ongoingOrUpcomingOpenHouses?: OpenHouse[],
  instantsSlots = getInstantTimeSlots(ongoingOrUpcomingOpenHouses).instantSlots,
  timezone = DEFAULT_TIMEZONE,
  now = moment.tz(timezone),
) {
  const closestSlots = take(instantsSlots, 2).map((slot) => ({ slot, slotExists: true }));
  if (!closestSlots.length) {
    const preferredSlots = slots.filter((s) =>
      isPreferredExclusiveTourRequestTimeSlot(s, timezone),
    );
    let i = 0;
    while (closestSlots.length != 2 && i < preferredSlots.length) {
      const preferredSpotStartTime = moment(preferredSlots[i].start_time).tz(timezone);
      if (
        !preferredSpotStartTime.isSame(now, 'day') &&
        !closestSlots.some(({ slot }) => slot === preferredSlots[i])
      ) {
        closestSlots.push({
          slot: preferredSlots[i],
          slotExists: false,
        });
      }
      i++;
    }
  }
  return orderBy(closestSlots, ['slot.start_time']);
}

export function isPreferredExclusiveTourRequestTimeSlot(
  slot: ExclusiveTourRequestTimeSlot,
  timezone = DEFAULT_TIMEZONE,
) {
  const startTime = moment(slot.start_time).tz(timezone);
  return startTime.hour() >= 16 || startTime.day() == 6 || startTime.day() == 0;
}

export function getInstantTimeSlots(
  ongoingOrUpcomingOpenHouses?: OpenHouse[],
  timezone = DEFAULT_TIMEZONE,
) {
  if (!ongoingOrUpcomingOpenHouses) {
    return { instantSlots: [], ongoingOrUpcomingOpenHousesMoment: [] };
  }
  const ongoingOrUpcomingOpenHousesMoment = ongoingOrUpcomingOpenHouses.map(
    ({ start_time, end_time }) => ({
      startTime: moment(start_time).tz(timezone),
      endTime: moment(end_time).tz(timezone),
    }),
  );
  const instantSlots = ongoingOrUpcomingOpenHousesMoment.map((oh) => ({
    start_time: oh.startTime.toISOString(),
    end_time: oh.endTime.toISOString(),
  }));
  return { instantSlots, ongoingOrUpcomingOpenHousesMoment };
}

export function arrayIncludeSlot(
  slots: { start_time: string; end_time: string }[],
  slot: { start_time: string; end_time: string },
) {
  return slots.some((s) => s.start_time == slot.start_time && s.end_time == slot.end_time);
}

export interface TimeSlotMoment {
  startTime: Moment;
  endTime: Moment;
}

export function doesOpenHouseOverlapTimeSlot(slot: TimeSlotMoment, openHouse: TimeSlotMoment) {
  return (
    slot.startTime.isBetween(openHouse.startTime, openHouse.endTime, undefined, '[)') ||
    slot.endTime.isBetween(openHouse.startTime, openHouse.endTime, undefined, '(]')
  );
}

export const REQUEST_TOUR_ERROR_MESSAGES = {
  generic: 'There was an issue with your tour request. Please try again.',
  tourSpotUnavailable: 'Tour slot you requested no longer exists. Please select other times.',
  alreadyRequestedATour:
    'You already requested a tour for this home. Check email and text for details.',
} as const;
